import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getV2TenantGroups, type V2TenantGroupListResponse } from '@/api/white-labels';
import { getSchool, getSchoolOptions } from '@/api/schools';
import { createSchoolNetwork, attachSchoolToNetwork } from '@/api/school-networks';
import { deployV2, getV2Progress } from '@/api/v2-deployment';
import { getEstablishments, importV1Establishments } from '@/api/establishments';
import { V2DeploymentStatus } from './V2DeploymentStatus';
import { V2SubscriptionForm, type V2SubscriptionFormData } from './V2SubscriptionForm';
import {
  PhysicalNetworkSelector,
  defaultPhysicalNetworkChoice,
  type PhysicalNetworkChoice,
} from './PhysicalNetworkSelector';
import { attachWarningText } from '@/components/school-networks/AttachSchoolDialog';
import type { V2DeployRequest, V2SetupStatus, V2DeploymentInfo } from '@/types/v2-deployment';
import type { SchoolOptions, Establishment, School } from '@/types/school';
import { Loader2, Server, AlertTriangle, Building2, Download, Ticket } from 'lucide-react';

interface V2DeployDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolCode: string;
  schoolName: string;
  onSuccess?: () => void;
  /** Optional config to deploy immediately without showing form */
  deployConfig?: V2DeployRequest;
  /**
   * L'école doit rejoindre un réseau physique même sans marque blanche
   * détectée (ex. wizard : « marque blanche » cochée au branding).
   */
  requiresPhysicalNetwork?: boolean;
  /** L'école a un environnement V1 : propose l'import des établissements V1 */
  hasV1?: boolean;
}

type DialogStep = 'loading' | 'form' | 'deploying' | 'progress';

function extractApiError(err: unknown): string {
  const axiosError = err as { response?: { data?: { error?: string; details?: string } } };
  return (
    axiosError.response?.data?.error ||
    axiosError.response?.data?.details ||
    (err as Error).message ||
    'Erreur inattendue'
  );
}

export function V2DeployDialog({
  open,
  onOpenChange,
  schoolCode,
  schoolName,
  onSuccess,
  deployConfig,
  requiresPhysicalNetwork = false,
  hasV1 = false,
}: V2DeployDialogProps) {
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<DialogStep>('loading');
  const [setupStatus, setSetupStatus] = useState<V2SetupStatus | null>(null);
  const [existingDeployment, setExistingDeployment] = useState<V2DeploymentInfo | null>(null);
  const deployTriggeredRef = useRef(false);

  // École (réseau physique, marque blanche V1, id)
  const [school, setSchool] = useState<School | null>(null);

  // Tenant groups (réseaux physiques + groupe par défaut)
  const [tenantGroups, setTenantGroups] = useState<V2TenantGroupListResponse['data']>([]);
  const [defaultTenantGroupId, setDefaultTenantGroupId] = useState<string>('');
  const [schoolOptions, setSchoolOptions] = useState<SchoolOptions | null>(null);
  const [formData, setFormData] = useState<V2SubscriptionFormData>({
    offerId: null,
    optionIds: [],
    moduleIds: [],
  });
  const [networkChoice, setNetworkChoice] = useState<PhysicalNetworkChoice>(
    defaultPhysicalNetworkChoice(false)
  );
  const [confirmingAttach, setConfirmingAttach] = useState(false);

  // Établissements ADM (recréés sur le tenant à la fin du setup V2)
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [importingV1, setImportingV1] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  // État réseau dérivé de l'école
  const physicalNetwork = school?.network?.type === 'physical' ? school.network : null;
  const hasWhiteLabel = !!school?.whiteLabelEntity;
  const mustJoinNetwork = !physicalNetwork && (hasWhiteLabel || requiresPhysicalNetwork);
  const networkNotInitialized =
    !!physicalNetwork && !tenantGroups.some((g) => g.networkId === physicalNetwork.id);

  const handleImportV1 = async () => {
    setImportingV1(true);
    setImportMessage(null);
    setError(null);
    try {
      const result = await importV1Establishments(schoolCode);
      setEstablishments(result.establishments);
      setImportMessage(result.message);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setImportingV1(false);
    }
  };

  // Auto-deploy function
  const triggerDeploy = async (config: V2DeployRequest) => {
    if (deployTriggeredRef.current) return;
    deployTriggeredRef.current = true;

    setStep('deploying');
    setError(null);

    try {
      await deployV2(schoolCode, config);
      setStep('progress');
    } catch (err) {
      setError(extractApiError(err));
      setStep('form');
      deployTriggeredRef.current = false;
    }
  };

  /**
   * Abonnement choisi à l'étape précédente du wizard.
   *
   * Repris dès l'ouverture, INDÉPENDAMMENT des chargements ci-dessous : il
   * vient du wizard, aucun appel réseau n'est nécessaire pour le connaître.
   * Il était auparavant recopié dans le `.then()` du `Promise.all`, donc perdu
   * dès qu'un de ces appels échouait (`getSchoolOptions`, `getV2TenantGroups`) —
   * le dialog basculait alors sur son formulaire avec un `formData` vide et
   * « Déployer » envoyait `{}` : école déployée sans offre ni modules, en
   * silence, à charge de l'opérateur de tout resaisir depuis la fiche école.
   */
  useEffect(() => {
    if (!open) return;
    setFormData({
      offerId: deployConfig?.offerId ?? null,
      optionIds: deployConfig?.optionIds ?? [],
      moduleIds: deployConfig?.moduleIds ?? [],
    });
  }, [open, deployConfig]);

  // Check existing deployment and load data when dialog opens
  useEffect(() => {
    if (open) {
      setStep('loading');
      setError(null);
      setSetupStatus(null);
      setLoading(true);
      setExistingDeployment(null);
      deployTriggeredRef.current = false;

      Promise.all([
        getV2Progress(schoolCode).catch(() => null),
        getV2TenantGroups(),
        // Catalogue offres/options : ne sert qu'à AFFICHER des libellés quand
        // l'abonnement vient du wizard. Son échec ne doit donc pas faire
        // basculer tout le dialog en erreur — sinon l'opérateur voit un « 500 »
        // qui laisse croire que le déploiement a raté. Le cas où le catalogue
        // est réellement nécessaire (choix de l'offre dans ce dialog, sans
        // `deployConfig`) est signalé plus bas.
        getSchoolOptions().catch(() => null),
        getEstablishments(schoolCode).catch(() => []),
        getSchool(schoolCode).catch(() => null),
      ])
        .then(([deploymentInfo, groupsResult, optionsResult, establishmentsResult, schoolResult]) => {
          setEstablishments(establishmentsResult);
          setImportMessage(null);
          setSchool(schoolResult);
          setTenantGroups(groupsResult.data);
          const defaultId = groupsResult.defaultGroupId || '';
          setDefaultTenantGroupId(defaultId);
          setSchoolOptions(optionsResult);
          // Catalogue indisponible ET nécessaire (l'offre se choisit ici) :
          // il faut le dire. Avec `deployConfig`, l'offre est déjà choisie et
          // sera appliquée — le rappel en lecture seule l'indique, pas d'alerte.
          if (optionsResult === null && !deployConfig) {
            setError(
              "Le catalogue des offres n'a pas pu être chargé : rechargez la page pour pouvoir choisir une offre."
            );
          }

          const schoolPhysicalNetwork =
            schoolResult?.network?.type === 'physical' ? schoolResult.network : null;
          const schoolHasWhiteLabel = !!schoolResult?.whiteLabelEntity;
          const needsNetwork =
            !schoolPhysicalNetwork && (schoolHasWhiteLabel || requiresPhysicalNetwork);

          setNetworkChoice(defaultPhysicalNetworkChoice(needsNetwork));
          // `formData` est déjà initialisé depuis `deployConfig` par l'effet
          // dédié ci-dessus — ne pas le refaire ici, sinon l'opérateur qui a
          // ajusté l'offre dans ce formulaire pendant le chargement la perdrait.

          if (groupsResult.warning) {
            setError(groupsResult.warning);
          }

          if (deploymentInfo?.deployed && deploymentInfo?.tenantId) {
            setExistingDeployment(deploymentInfo);
            setSetupStatus(deploymentInfo.progress?.status || deploymentInfo.localStatus || null);
            setStep('progress');
          } else if (deployConfig && !needsNetwork) {
            // Auto-deploy : uniquement quand aucun choix de réseau n'est
            // requis (école déjà dans un réseau physique — le backend
            // résout son groupe — ou école sans marque blanche → défaut).
            triggerDeploy({ ...deployConfig, tenantGroupId: undefined });
          } else {
            setStep('form');
          }
        })
        .catch((err) => {
          setError(extractApiError(err));
          setStep('form');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, schoolCode, deployConfig, requiresPhysicalNetwork]);

  const buildDeployRequest = (): V2DeployRequest => ({
    offerId: formData.offerId || undefined,
    optionIds: formData.optionIds.length > 0 ? formData.optionIds : undefined,
    moduleIds: formData.moduleIds.length > 0 ? formData.moduleIds : undefined,
  });

  /** Validation avant déploiement ; retourne le message d'erreur éventuel */
  const validateChoice = (): string | null => {
    if (physicalNetwork) return null;
    if (networkChoice.mode === 'default') {
      return mustJoinNetwork
        ? 'Une école en marque blanche doit rejoindre un réseau physique.'
        : null;
    }
    if (networkChoice.mode === 'existing' && !networkChoice.networkId) {
      return 'Sélectionnez un réseau physique.';
    }
    if (networkChoice.mode === 'new' && (!networkChoice.newNetworkName.trim() || !networkChoice.newNetworkHost.trim())) {
      return 'Renseignez le nom et le domaine du nouveau réseau.';
    }
    if (!networkChoice.schoolHost.trim()) {
      return 'Renseignez le domaine (host) de l\'école.';
    }
    return null;
  };

  const handleDeployClick = () => {
    const validationError = validateChoice();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    if (!physicalNetwork && networkChoice.mode !== 'default') {
      // Rattachement irréversible : confirmation explicite d'abord
      setConfirmingAttach(true);
      return;
    }

    handleDeploy();
  };

  /** Séquence complète : (créer réseau) → rattacher → déployer */
  const handleDeploy = async () => {
    setConfirmingAttach(false);
    setDeploying(true);
    setError(null);

    try {
      if (!physicalNetwork && networkChoice.mode !== 'default') {
        if (!school?.id) {
          throw new Error('École introuvable : impossible de la rattacher au réseau.');
        }

        let networkId = networkChoice.networkId;
        if (networkChoice.mode === 'new') {
          const created = await createSchoolNetwork({
            type: 'physical',
            name: networkChoice.newNetworkName.trim(),
            host: networkChoice.newNetworkHost.trim(),
          });
          networkId = created.id;
          // Si le rattachement échoue ensuite, le réseau créé est re-proposé
          setNetworkChoice((prev) => ({ ...prev, mode: 'existing', networkId: created.id }));
          const groups = await getV2TenantGroups();
          setTenantGroups(groups.data);
        }

        await attachSchoolToNetwork(networkId!, school.id, networkChoice.schoolHost.trim());
      }

      setStep('deploying');
      // Le backend résout le tenant group (réseau physique ou défaut)
      await deployV2(schoolCode, buildDeployRequest());
      setStep('progress');
    } catch (err) {
      setError(extractApiError(err));
      setStep('form');
    } finally {
      setDeploying(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (step === 'progress' || existingDeployment) {
      onSuccess?.();
    }
  };

  const handleStatusChange = (status: V2SetupStatus | null) => {
    setSetupStatus(status);
  };

  const getDialogTitle = () => {
    if (step === 'deploying') return 'Deploiement V2 en cours';
    if (step === 'progress') {
      if (setupStatus === 'failed') return 'Deploiement V2 echoue';
      if (setupStatus === 'completed') return 'Deploiement V2 termine';
      if (setupStatus === 'in_progress') return 'Deploiement V2 en cours';
      return 'Statut du deploiement V2';
    }
    return 'Deployer en V2';
  };

  const getDialogDescription = () => {
    if (step === 'deploying') {
      return (
        <>
          Deploiement de <strong>{schoolName}</strong> en cours...
        </>
      );
    }
    if (step === 'progress') {
      if (setupStatus === 'failed') {
        return (
          <>
            Le deploiement de <strong>{schoolName}</strong> a echoue. Vous pouvez relancer.
          </>
        );
      }
      return (
        <>
          Suivi du deploiement de <strong>{schoolName}</strong>
        </>
      );
    }
    return (
      <>
        Déployer l'école <strong>{schoolName}</strong> sur l'infrastructure V2
      </>
    );
  };

  const selectedNetworkName =
    networkChoice.mode === 'new'
      ? networkChoice.newNetworkName.trim()
      : tenantGroups.find((g) => g.networkId === networkChoice.networkId)?.name || '';

  // Offre déjà choisie en amont (wizard) : on la rappelle en lecture seule au
  // lieu de la redemander. On n'ouvre le formulaire d'offre que si le dialog
  // est ouvert seul (ex. « Corriger » un déploiement en échec).
  const offerPrechosen = !!deployConfig;
  const selectedOffer = schoolOptions?.offers.find((o) => o.id === formData.offerId) || null;
  const summaryModules = selectedOffer
    ? selectedOffer.modules.filter((m) => m.isDefault || formData.moduleIds.includes(m.id))
    : [];
  const summaryOptions =
    schoolOptions?.productOptions.filter((po) => formData.optionIds.includes(po.id)) || [];

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          {/* Loading state */}
          {step === 'loading' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Chargement...</span>
            </div>
          )}

          {/* Deploying state - show immediately when deploy starts */}
          {step === 'deploying' && (
            <div className="py-4">
              <V2DeploymentStatus
                schoolCode={schoolCode}
                onStatusChange={handleStatusChange}
                autoRefresh={true}
                refreshInterval={1000}
              />
            </div>
          )}

          {/* Form step */}
          {step === 'form' && (
            <div className="space-y-4">
              {/* École déjà dans un réseau physique : cible figée */}
              {physicalNetwork && !networkNotInitialized && (
                <Alert>
                  <Server className="h-4 w-4" />
                  <AlertDescription>
                    Cette école appartient au réseau physique{' '}
                    <strong>{physicalNetwork.name}</strong>
                    {physicalNetwork.v2Host && (
                      <span className="font-mono"> ({physicalNetwork.v2Host})</span>
                    )}
                    {' '}: son tenant group est imposé.
                  </AlertDescription>
                </Alert>
              )}

              {/* Réseau physique V1-legacy non initialisé : blocage */}
              {physicalNetwork && networkNotInitialized && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Le réseau physique <strong>{physicalNetwork.name}</strong> n'est pas encore
                    initialisé sur la V2. Créez son tenant group depuis la page du réseau avant de
                    déployer cette école.
                  </AlertDescription>
                </Alert>
              )}

              {/* École en marque blanche sans réseau : choix obligatoire */}
              {mustJoinNetwork && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Cette école est en marque blanche : elle doit rejoindre un{' '}
                    <strong>réseau physique</strong> (jamais le groupe École Futée par défaut). Le
                    rattachement est définitif.
                  </AlertDescription>
                </Alert>
              )}

              {/* Sélecteur de réseau (masqué si l'école a déjà son réseau) */}
              {!physicalNetwork && (
                <PhysicalNetworkSelector
                  value={networkChoice}
                  onChange={setNetworkChoice}
                  groups={tenantGroups}
                  requiresNetwork={mustJoinNetwork}
                />
              )}

              {offerPrechosen ? (
                /* Offre reprise du parcours amont : rappel en lecture seule */
                <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Ticket className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Offre</span>
                  </div>
                  {selectedOffer ? (
                    <>
                      <p className="text-sm">
                        {selectedOffer.productName} — <strong>{selectedOffer.name}</strong>
                      </p>
                      {summaryModules.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {summaryModules.map((m) => (
                            <Badge key={m.id} variant="secondary">
                              {m.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {summaryOptions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {summaryOptions.map((o) => (
                            <Badge key={o.id} variant="outline">
                              {o.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </>
                  ) : formData.offerId ? (
                    // Offre bien choisie, mais catalogue non chargé (l'appel
                    // `options` a échoué) : ne pas laisser croire qu'aucune offre
                    // n'est retenue — elle sera bien envoyée au déploiement.
                    <p className="text-sm text-muted-foreground">
                      Offre sélectionnée à l'étape précédente (libellé indisponible, catalogue non
                      chargé). Elle sera appliquée au déploiement.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucune offre sélectionnée.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Offre choisie à l'étape précédente. Pour la modifier, revenez au formulaire de
                    déploiement.
                  </p>
                </div>
              ) : (
                <V2SubscriptionForm
                  options={schoolOptions}
                  tenantGroups={[]}
                  defaultTenantGroupId={defaultTenantGroupId}
                  value={formData}
                  onChange={setFormData}
                  showTenantGroup={false}
                />
              )}

              {/* Établissements (facultatif) : recréés sur le tenant en fin de setup */}
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Établissements</span>
                    <span className="text-muted-foreground">
                      {establishments.length} établissement{establishments.length > 1 ? 's' : ''} sera
                      {establishments.length > 1 ? 'ont' : ''} recréé{establishments.length > 1 ? 's' : ''} sur
                      le tenant V2
                    </span>
                  </div>
                  {hasV1 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleImportV1}
                      disabled={importingV1}
                    >
                      {importingV1 ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5 mr-1" />
                      )}
                      Importer depuis V1
                    </Button>
                  )}
                </div>
                {importMessage && <p className="text-xs text-muted-foreground">{importMessage}</p>}
                <p className="text-xs text-muted-foreground">
                  Facultatif : vous pouvez compléter les informations (RNE, adresse, type) dans
                  l'onglet « Établissements » avant de déployer, ou les synchroniser plus tard.
                </p>
              </div>

              {/* Error message */}
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Progress step - show deployment status */}
          {step === 'progress' && (
            <div className="py-4">
              <V2DeploymentStatus
                schoolCode={schoolCode}
                onStatusChange={handleStatusChange}
                autoRefresh={true}
                refreshInterval={1000}
              />

              {/* Error message */}
              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="flex-shrink-0 flex-col sm:flex-row gap-2 border-t pt-4">
          {step === 'form' && (
            <>
              <Button variant="outline" onClick={handleClose} disabled={deploying}>
                Annuler
              </Button>
              <Button
                onClick={handleDeployClick}
                disabled={loading || deploying || (!!physicalNetwork && networkNotInitialized)}
              >
                {deploying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deploiement...
                  </>
                ) : (
                  <>
                    <Server className="h-4 w-4 mr-2" />
                    Deployer
                  </>
                )}
              </Button>
            </>
          )}
          {(step === 'deploying' || step === 'progress') && (
            <Button variant="outline" onClick={handleClose}>
              {setupStatus === 'completed' ? 'Termine' : 'Fermer'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Confirmation d'irréversibilité du rattachement */}
    <AlertDialog open={confirmingAttach} onOpenChange={setConfirmingAttach}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Rattachement définitif au réseau
          </AlertDialogTitle>
          <AlertDialogDescription>
            {attachWarningText(
              schoolName,
              selectedNetworkName || 'le réseau sélectionné',
              networkChoice.schoolHost.trim()
            )}{' '}
            Le déploiement V2 sera ensuite lancé dans le tenant group de ce réseau.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deploying}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeploy}
            disabled={deploying}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            {deploying ? 'En cours...' : 'Rattacher et déployer'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
